import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild
} from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { RemoteData } from 'ngx-remotedata';
import {
    AixAssignBoxTemplateComponent,
    AixButtonComponent,
    AixDataTestingDirective,
    AixDropdownComponent,
    AixHeaderSectionComponent,
    AixInputComponent,
    AixModalComponent,
    AixNotificationComponent,
    AixRichTextComponent,
    AixTextareaComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { AixErrorBoxComponent } from '../error-box/error-box';
import { ErrorWrapper } from '../../store/models/common';
import { ProfileStoreFacade } from '@advisor-ui/app-services';
import { CustomTemplate } from '../../store/custom-templates/model';
import {
    CreateCustomTemplateAction,
    CreateCustomTemplateSuccessAction,
    DeleteCustomTemplateAction,
    DeleteCustomTemplateFailureAction,
    DeleteCustomTemplateSuccessAction,
    LoadCustomTemplatesAction,
    LoadCustomTemplatesSuccessAction,
    UpdateCustomTemplateAction,
    UpdateCustomTemplateSuccessAction
} from '../../store/custom-templates/actions';
import { AixEffectActions } from '../../store/utils/aix-dispatcher.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/reducers';
import { ENVIRONMENT, extractStreamValue, IEnvironment } from '@trade-platform/ui-utils';
import { getDataFromState } from '../remote-data/utils';
import { EmailSettings } from '../../store/firm/model';

export interface DocuSignForm {
    orderId?: string;
    emailSubject: string;
    emailBody: string;
}

@Component({
    selector: 'aix-send-message',
    templateUrl: './aix-send-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        AixHeaderSectionComponent,
        AixButtonComponent,
        AixNotificationComponent,
        AixInputComponent,
        AixDropdownComponent,
        AixDataTestingDirective,
        AixAssignBoxTemplateComponent,
        AixModalComponent,
        AixErrorBoxComponent,
        AixRichTextComponent,
        AixTextareaComponent
    ]
})
export class AixSendMessageComponent implements OnInit, OnDestroy {
    private environment = inject<IEnvironment>(ENVIRONMENT);
    private cd = inject(ChangeDetectorRef);
    store = inject<Store<AppState>>(Store);
    profileStoreFacade = inject(ProfileStoreFacade);
    actions$ = inject(AixEffectActions);

    type = input.required<'PPM' | 'ESign'>();
    formData = input<{ subject: string; body: string }>();
    description = input<string>('');
    useRichText = input<boolean>(true);
    states = input.required<Observable<RemoteData<any, string | ErrorWrapper>>[]>();
    recipientError = input<string>('');
    recipients = computed(() => this.recipientError().split(','));
    isAssignBoxOpen = signal<boolean>(false);
    emailSettings = input<EmailSettings | undefined>({
        disclosureEnabled: false,
        disclosureMessage: ''
    });

    removeTemplateModal = viewChild<AixModalComponent>('removeTemplateModal');
    saveTemplateNotificationRef = viewChild<AixNotificationComponent>(
        'saveTemplateNotificationRef'
    );
    removeTemplateNotificationRef = viewChild<AixNotificationComponent>(
        'removeTemplateNotificationRef'
    );

    editOrder = output();

    showDisclosure = false;

    subscriptions: Subscription[] = [];

    templates: CustomTemplate[] = [];
    selectedTemplateId?: number;
    selectedTemplate?: CustomTemplate;
    isCreatingTemplate = false;
    isDefaultTemplate = false;
    selectPrimaryTemplate = true;

    docusignEmail: string;
    sendForm: UntypedFormGroup = new UntypedFormGroup({
        emailTemplate: new UntypedFormControl({ value: this.templates[0] }, [Validators.required]),
        emailSubject: new UntypedFormControl(null, [
            Validators.required,
            Validators.maxLength(100)
        ]),
        emailBody: new UntypedFormControl(null, [Validators.required])
    });

    templateButtonType = BUTTON_TYPE.link;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.docusignEmail = this.environment.contactDetails.docusignEmail;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.sendForm.valueChanges.subscribe(value => {
                this.cd.detectChanges();
            }),
            this.actions$
                .ofClass<LoadCustomTemplatesSuccessAction>(LoadCustomTemplatesSuccessAction)
                .subscribe(action => {
                    this.templates = action.payload;

                    if (this.selectPrimaryTemplate) {
                        this.selectPrimaryTemplate = false;
                        this.selectedTemplateId = this.templates.find(t => t.isPrimary)?.id;
                    }

                    this.templates = this.templates.sort((a, b) => (a.name ? -1 : 1));

                    const defaultTemplate = this.createDefaultTemplate();
                    this.templates.unshift(defaultTemplate);

                    const selectedTemplate = this.templates.find(
                        t => t.id === this.selectedTemplateId
                    );
                    const template = selectedTemplate ?? defaultTemplate;

                    this.isDefaultTemplate = template.id === 0;

                    this.sendForm.get('emailTemplate')?.setValue(template);
                    this.setTemplate(template);

                    setTimeout(() => this.saveTemplateNotificationRef()?.closeNotification(), 1000);
                    setTimeout(
                        () => this.removeTemplateNotificationRef()?.closeNotification(),
                        1000
                    );
                }),
            this.actions$
                .ofClass<CreateCustomTemplateSuccessAction>(CreateCustomTemplateSuccessAction)
                .subscribe(action => {
                    this.selectedTemplateId = action.payload.id;
                    this.onSaveTemplate();
                    this.store.dispatch(new LoadCustomTemplatesAction({ type: this.type() }));
                }),
            this.actions$
                .ofClass<UpdateCustomTemplateSuccessAction>(UpdateCustomTemplateSuccessAction)
                .subscribe(action => {
                    this.selectedTemplateId = action.payload.id;
                    this.onSaveTemplate();
                    this.store.dispatch(new LoadCustomTemplatesAction({ type: this.type() }));
                }),
            this.actions$
                .ofClass<DeleteCustomTemplateSuccessAction>(DeleteCustomTemplateSuccessAction)
                .subscribe(action => {
                    this.selectPrimaryTemplate = true;
                    this.removeTemplateNotificationRef()?.openNotification();
                    this.store.dispatch(new LoadCustomTemplatesAction({ type: this.type() }));
                }),
            this.actions$
                .ofClass<DeleteCustomTemplateFailureAction>(DeleteCustomTemplateFailureAction)
                .subscribe(action => {
                    this.selectPrimaryTemplate = true;
                    this.removeTemplateNotificationRef()?.openNotification();
                    this.store.dispatch(new LoadCustomTemplatesAction({ type: this.type() }));
                })
        );

        this.store.dispatch(new LoadCustomTemplatesAction({ type: this.type() }));
    }

    createDefaultTemplate() {
        const template: CustomTemplate = {
            id: 0,
            userId: 0,
            type: '',
            isPrimary: true,
            body: '',
            subject: '',
            name: 'System Default',
            createdAt: ''
        };

        if (this.type() === 'ESign') {
            const order$ = getDataFromState(this.store.select(state => state.order));
            const order = extractStreamValue(order$);
            let advisor;
            let advisorSignature;
            if (order.advisor && order.advisor.id && order.advisor.firstName) {
                advisor = `${order.advisor.firstName} ${order.advisor.lastName}`;
                advisorSignature = `${advisor}\n${order.advisor.email}`;
            }

            template.type = 'ESign';
            template.subject = advisor
                ? `${advisor} Sent Documents for Review`
                : `Documents for ${order.fund.name}`;
            const signature = advisorSignature ? `\n\nThank you,\n${advisorSignature}` : '';
            template.body = `Please review the investment documents for ${order.fund.name}.${signature}`;
        } else {
            template.type = 'PPM';
            template.subject = this.formData()?.subject as string;
            template.body = this.formData()?.body as string;
        }

        return template;
    }

    onEditOrder() {
        this.editOrder.emit();
    }

    onHideAssignBoxTemplate() {
        setTimeout(() => {
            this.isAssignBoxOpen.set(false);
            this.isCreatingTemplate = false;
            this.cd.detectChanges();
        }, 1);
    }

    onSaveAssignBoxTemplate(template: CustomTemplate) {
        if (this.isCreatingTemplate) {
            this.store.dispatch(new CreateCustomTemplateAction({ ...template, type: this.type() }));
        } else {
            this.store.dispatch(new UpdateCustomTemplateAction({ ...template, type: this.type() }));
        }
    }

    onSaveTemplate() {
        this.saveTemplateNotificationRef()?.openNotification();
        setTimeout(() => {
            this.isAssignBoxOpen.set(false);
            this.cd.detectChanges();
        }, 1);
    }

    editTemplate() {
        this.selectedTemplate = this.templates.find(
            t => t.id === this.selectedTemplateId
        ) as CustomTemplate;
        this.isAssignBoxOpen.set(true);
        this.isCreatingTemplate = false;
    }

    removeTemplate() {
        this.isAssignBoxOpen.set(false);
        this.removeTemplateModal()?.openModal();
    }

    confirmRemoveTemplate(e: string) {
        switch (e) {
            case 'Yes, continue':
                if (this.selectedTemplateId) {
                    this.store.dispatch(new DeleteCustomTemplateAction(this.selectedTemplateId));
                }
                break;
            default:
                break;
        }
    }

    createTemplate() {
        this.selectedTemplate = undefined;
        this.isAssignBoxOpen.set(true);
        this.isCreatingTemplate = true;
    }

    onTemplateChange(template: CustomTemplate[]) {
        if (template?.length) {
            this.isDefaultTemplate = template[0].id === 0;
            this.setTemplate(template[0]);
        }
    }

    setTemplate(template: CustomTemplate) {
        if (template) {
            this.selectedTemplateId = template.id;
            (this.sendForm.get('emailSubject') as UntypedFormControl).setValue(template.subject);
            (this.sendForm.get('emailBody') as UntypedFormControl).setValue(template.body);
            this.sendForm.updateValueAndValidity();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
